import { z } from 'zod';
import type { DecorationsLayer } from './schemas/decorations.schema.js';
import { PasswordSchema } from './schemas/password.schema.js';

// User login validation
export const LoginUserSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required')
});

// Valid character IDs for avatar validation
export const VALID_CHARACTER_IDS = [
  'premade_01', 'premade_02', 'premade_03', 'premade_04', 'premade_05',
  'premade_06', 'premade_07', 'premade_08', 'premade_09', 'premade_10',
  'premade_11', 'premade_12', 'premade_13', 'premade_14', 'premade_15',
  'premade_16', 'premade_17', 'premade_18', 'premade_19', 'premade_20'
] as const;

// User profile update validation
export const UpdateUserSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be at most 50 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .optional(),
  email: z.string().email('Invalid email format').max(255).optional(),
  avatar: z.enum(VALID_CHARACTER_IDS, {
    errorMap: () => ({ message: 'Invalid character selection' })
  }).optional(),
  currentPassword: z.string().min(1).optional(),
  newPassword: PasswordSchema.optional()
}).refine((data) => {
  // If newPassword is provided, currentPassword must also be provided
  if (data.newPassword && !data.currentPassword) {
    return false;
  }
  return true;
}, {
  message: 'Current password is required when changing password',
  path: ['currentPassword']
});

// Type inference from schemas
export type LoginUserInput = z.infer<typeof LoginUserSchema>;
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  /** Machine-readable failure reason, e.g. `guest_expired`, `last_admin`. */
  code?: string;
}

export interface UserData {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  /** Platform-wide role; drives the superadmin console entry point. */
  platformRole: PlatformRole;
  createdAt: string;
  lastLogin?: string;
}

export interface AuthResponse {
  user: UserData;
  token: string;
}

// Error types
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ========================================
// MULTIPLAYER TYPES AND SCHEMAS
// ========================================

// Enum types
export enum RoomRole {
  ADMIN = 'admin',
  PLAYER = 'player',
  /** Time-boxed island access; the permission always carries an `expiresAt`. */
  GUEST = 'guest'
}

/** Platform-wide role, independent of any island role. */
export enum PlatformRole {
  USER = 'user',
  SUPERADMIN = 'superadmin'
}

/** Guest access durations, in days, offered when inviting or extending a guest. */
export const GUEST_DAY_OPTIONS = [1, 7, 30] as const;
export type GuestDays = (typeof GUEST_DAY_OPTIONS)[number];

export const GuestDaysSchema = z.union([z.literal(1), z.literal(7), z.literal(30)], {
  errorMap: () => ({ message: `Guest days must be one of ${GUEST_DAY_OPTIONS.join(', ')}` })
});

/** Superadmin console lists return at most this many rows plus `truncated`. */
export const ADMIN_LIST_LIMIT = 500;

export enum PlayerStatus {
  AVAILABLE = 'available',
  BUSY = 'busy',
  DND = 'dnd'
}

/** Organization names are unique case-insensitively and at most 100 characters. */
export const ORGANIZATION_NAME_MAX_LENGTH = 100;

// Room validation schemas

/**
 * Creating an island always names its organization (§3.1 invariant 1): either
 * an existing one the creator may create in (§4.2), or a brand new one. Exactly
 * one of the two fields is accepted — naming both, or neither, is a 400.
 */
export const CreateRoomSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Room name is required')
      .max(100, 'Room name must be at most 100 characters'),
    description: z
      .string()
      .max(500, 'Description must be at most 500 characters')
      .optional(),
    maxPlayers: z
      .number()
      .int('Max players must be an integer')
      .min(1, 'Room must allow at least 1 player')
      .max(100, 'Room cannot exceed 100 players')
      .optional()
      .default(100),
    organizationId: z.string().uuid('Invalid organization ID').optional(),
    newOrganizationName: z
      .string()
      .trim()
      .min(1, 'Organization name is required')
      .max(
        ORGANIZATION_NAME_MAX_LENGTH,
        `Organization name must be at most ${ORGANIZATION_NAME_MAX_LENGTH} characters`
      )
      .optional()
  })
  .refine(
    data => (data.organizationId ? 1 : 0) + (data.newOrganizationName ? 1 : 0) === 1,
    { message: 'Choose an organization or name a new one', path: ['organizationId'] }
  );

/**
 * Island settings an admin may edit. The organization is deliberately absent:
 * only superadmins move an island, through the console (§4.2).
 */
export const UpdateRoomSchema = z.strictObject({
  name: z
    .string()
    .min(1, 'Room name is required')
    .max(100, 'Room name must be at most 100 characters')
    .optional(),
  description: z
    .string()
    .max(500, 'Description must be at most 500 characters')
    .optional(),
  maxPlayers: z
    .number()
    .int('Max players must be an integer')
    .min(1, 'Room must allow at least 1 player')
    .max(100, 'Room cannot exceed 100 players')
    .optional()
});

/**
 * Permission update: either a new role, or an extension of a guest's access.
 * Exactly one shape is accepted; `'role' in input` discriminates them.
 */
export const UpdatePermissionSchema = z.union(
  [
    // Strict on both sides: `{ role, extendGuestDays }` asks for two different
    // changes at once, and silently honouring one of them is worse than a 400.
    z.strictObject({
      role: z.nativeEnum(RoomRole, { required_error: 'Role is required' })
    }),
    z.strictObject({
      extendGuestDays: GuestDaysSchema
    })
  ],
  {
    errorMap: () => ({
      message: `Provide either role or extendGuestDays (${GUEST_DAY_OPTIONS.join(', ')})`
    })
  }
);

/** Superadmin retargeting an island to another organization. */
export const SetRoomOrganizationSchema = z.object({
  organizationId: z.string().uuid('Invalid organization ID')
});

// WebSocket message schemas
export const PlayerMovementSchema = z.object({
  position: z.object({
    x: z.number(),
    y: z.number()
  }),
  velocity: z.object({
    x: z.number(),
    y: z.number()
  }).optional(),
  animation: z.string(),
  direction: z.enum(['up', 'down', 'left', 'right']),
  seated: z.boolean().optional(),
  decorationId: z.string().optional()
});

export const JoinRoomMessageSchema = z.object({
  roomId: z.string().uuid('Invalid room ID')
});

export const UserStatusChangeSchema = z.object({
  roomId: z.string().uuid('Invalid room ID'),
  status: z.enum(['available', 'busy', 'dnd'])
});

export const PlayerAvatarUpdateSchema = z.object({
  roomId: z.string().uuid('Invalid room ID'),
  avatar: z.enum(VALID_CHARACTER_IDS, {
    errorMap: () => ({ message: 'Invalid character selection' })
  })
});

export const PlayerTeleportSchema = z.object({
  targetUserId: z.string().uuid('Invalid user ID'),
  position: z.object({
    x: z.number(),
    y: z.number()
  }),
  roomId: z.string().uuid('Invalid room ID').optional()
});

// Type inference from schemas
export type CreateRoomInput = z.infer<typeof CreateRoomSchema>;
export type UpdateRoomInput = z.infer<typeof UpdateRoomSchema>;
export type UpdatePermissionInput = z.infer<typeof UpdatePermissionSchema>;
export type SetRoomOrganizationInput = z.infer<typeof SetRoomOrganizationSchema>;
export type PlayerMovementInput = z.infer<typeof PlayerMovementSchema>;
export type JoinRoomMessageInput = z.infer<typeof JoinRoomMessageSchema>;
export type UserStatusChangeInput = z.infer<typeof UserStatusChangeSchema>;
export type PlayerAvatarUpdateInput = z.infer<typeof PlayerAvatarUpdateSchema>;
export type PlayerTeleportInput = z.infer<typeof PlayerTeleportSchema>;

// Database entity types
export interface Room {
  id: string;
  name: string;
  description?: string;
  maxPlayers: number;
  creatorId: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  decorationsLayer?: DecorationsLayer; // Typed decorations object layer
  /** The single organization this island belongs to (spec §3.1 invariant 1). */
  organization?: { id: string; name: string };
}

export interface RoomPermission {
  id: string;
  roomId: string;
  userId: string;
  role: RoomRole;
  /** Required for guests, null for admins and residents (§3.1 invariant 3). */
  expiresAt?: string | null;
  grantedBy: string;
  createdAt: string;
}

export interface PlayerSession {
  id: string;
  userId: string;
  roomId: string;
  positionX: number;
  positionY: number;
  lastActive: string;
  isConnected: boolean;
  sessionValid: boolean;
  status: string;
}

// Extended types with relations
export interface RoomWithDetails extends Room {
  creator: UserData;
  permissions: RoomPermission[];
  playerSessions: PlayerSession[];
  currentUserRole?: RoomRole;
  /** Guest expiry for the requesting user; null for admins and residents. */
  expiresAt?: string | null;
}

export interface UserRoomInfo {
  room: Room;
  role: RoomRole;
  /** Same value as `role`, under the name the console and island list use. */
  currentUserRole?: RoomRole;
  /**
   * Guest expiry for the requesting user; null for admins and residents. A past
   * date means the guest lapsed: the island is listed as "Expired", not enterable.
   */
  expiresAt?: string | null;
  lastPosition?: { x: number; y: number };
}

// WebSocket message types
export interface BaseMessage {
  type: string;
  timestamp: number;
  userId: string;
  roomId: string;
  data?: any;
}

export interface JoinRoomMessage extends BaseMessage {
  type: 'join_room';
  data: {
    roomId: string;
  };
}

export interface PlayerMovementMessage extends BaseMessage {
  type: 'player_movement';
  data: {
    position: { x: number; y: number };
    velocity?: { x: number; y: number };
    animation: string;
    direction: 'up' | 'down' | 'left' | 'right';
    seated?: boolean;
    decorationId?: string;
  };
}

export interface LeaveRoomMessage extends BaseMessage {
  type: 'leave_room';
}

export interface RoomStateMessage extends BaseMessage {
  type: 'room_state';
  data: {
    room: {
      id: string;
      name: string;
      maxPlayers: number;
    };
    players: Array<{
      userId: string;
      username: string;
      position: { x: number; y: number };
      animation: string;
      direction: string;
      role: RoomRole;
      status: PlayerStatus;
      avatar: string;
    }>;
    zones: HomeZone[];
    privateAreas?: PrivateArea[];
  };
}

export interface PlayerUpdateMessage extends BaseMessage {
  type: 'player_update';
  data: {
    userId: string;
    position: { x: number; y: number };
    animation: string;
    direction: string;
  };
}

export interface PlayerTeleportedMessage extends BaseMessage {
  type: 'player_teleported';
  data: {
    userId: string;
    position: { x: number; y: number };
    animation: string;
    direction: string;
  };
}

export interface PlayerJoinedMessage extends BaseMessage {
  type: 'player_joined';
  data: {
    userId: string;
    username: string;
    position: { x: number; y: number };
    role: RoomRole;
    avatar: string;
  };
}

export interface PlayerLeftMessage extends BaseMessage {
  type: 'player_left';
  data: {
    userId: string;
    username: string;
  };
}

export interface ErrorMessage extends BaseMessage {
  type: 'error';
  data: {
    code: string;
    message: string;
  };
}

export interface UserStatusChangeMessage extends BaseMessage {
  type: 'user_status_change';
  data: {
    status: PlayerStatus;
  };
}

export interface UserStatusUpdatedMessage extends BaseMessage {
  type: 'user_status_updated';
  data: {
    userId: string;
    username: string;
    status: PlayerStatus;
  };
}

export interface PlayerAvatarUpdateMessage extends BaseMessage {
  type: 'player_avatar_update';
  data: {
    avatar: string;
  };
}

export interface PlayerTeleportMessage extends BaseMessage {
  type: 'player_teleport';
  data: {
    targetUserId: string;
    position: { x: number; y: number };
  };
}

// Union type for all WebSocket messages
export type WebSocketMessage = 
  | JoinRoomMessage 
  | PlayerMovementMessage 
  | LeaveRoomMessage 
  | RoomStateMessage 
  | PlayerUpdateMessage 
  | PlayerTeleportedMessage
  | PlayerJoinedMessage 
  | PlayerLeftMessage 
  | ErrorMessage
  | UserStatusChangeMessage
  | UserStatusUpdatedMessage
  | PlayerAvatarUpdateMessage
  | PlayerTeleportMessage
  | ZoneClaimedMessage
  | ZoneUnclaimedMessage
  | PrivateAreaUpdateMessage
  | PlayerEnteredPrivateAreaMessage
  | PlayerLeftPrivateAreaMessage;

// API Response types for rooms
export interface CreateRoomResponse {
  room: Room;
}

export interface GetRoomsResponse {
  rooms: UserRoomInfo[];
}

export interface GetRoomDetailsResponse {
  room: RoomWithDetails;
  /**
   * Everyone holding a permission on the island, connected or not — what the
   * member list manages. `players` below is the live session view instead.
   *
   * Present only for actors who may manage members (admins and superadmins):
   * a resident has no business reading the island's roster.
   */
  members?: RoomMemberSummary[];
  players: Array<{
    userId: string;
    username: string;
    position: { x: number; y: number };
    role: RoomRole;
    isConnected: boolean;
    status: PlayerStatus;
    avatar: string;
  }>;
  zoneClaims: HomeZoneClaim[];  // Changed from zones to claims
}

// ========================================
// PRIVATE AREA TYPES
// ========================================

// Interface for tile coordinates
export interface TileCoordinate {
  x: number;
  y: number;
}

// Interface for private area bounds
export interface PrivateAreaBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

// Interface for private area data structure
export interface PrivateAreaData {
  id: string;
  roomId: string;
  areaId: string;
  name?: string;
  closed: boolean;
  isDoorOpen?: boolean;
  capacity: number;
  ownerId?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  bounds: {
    tiles: TileCoordinate[];
    worldBounds?: PrivateAreaBounds;
  };
  doorTiles?: TileCoordinate[];
  occupants: PrivateAreaOccupant[];
  createdAt: string;
}

// Interface for private area occupants
export interface PrivateAreaOccupant {
  userId: string;
  username: string;
  avatar: string;
  enteredAt: string;
}

// Interface for private area update messages
export interface PrivateAreaUpdate {
  areaId: string;
  action: 'player_entered' | 'player_left' | 'closed' | 'opened';
  userId?: string;
  area: PrivateAreaData;
}

// Server-side private area interface (used in services)
export interface ServerPrivateArea {
  id: string;
  roomId: string;
  areaId: string;
  name?: string;
  closed: boolean;
  isDoorOpen?: boolean;
  capacity: number;
  ownerId?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  bounds: {
    tiles: TileCoordinate[];
    worldBounds?: PrivateAreaBounds;
  };
  doorTiles?: TileCoordinate[];
  occupants: PrivateAreaOccupant[];
  createdAt: string;
}

// ========================================
// HOME ZONE TYPES AND SCHEMAS
// ========================================

// Zone status enum
export enum ZoneStatus {
  UNCLAIMED = 'UNCLAIMED',
  CLAIMED = 'CLAIMED'
}

// Zone claiming validation
export const ClaimZoneSchema = z.object({
  zoneId: z.string().min(1, 'Zone ID is required')
});

export type ClaimZoneInput = z.infer<typeof ClaimZoneSchema>;

// HomeZone interface - simplified to use rectangle bounds from tilemap
export interface HomeZone {
  id: string;          // Object ID from tilemap
  x: number;           // X position in pixels from tilemap
  y: number;           // Y position in pixels from tilemap
  width: number;       // Width in pixels from tilemap
  height: number;      // Height in pixels from tilemap
  // Claim data from database
  claimedBy?: string;
  claimedByUsername?: string;
  claimedByAvatar?: string;
  claimedByStatus?: string;
  claimedByIsConnected?: boolean;
  claimedAt?: string;
}

// Simplified claim data for database storage
export interface HomeZoneClaim {
  roomId: string;
  zoneId: string;      // Object ID from tilemap
  userId?: string;     // Who claimed it
  claimedAt?: string;
}

// PrivateArea interface (alias for PrivateAreaData for compatibility)
export interface PrivateArea extends PrivateAreaData {}

// Zone-related WebSocket message types
export interface ZoneClaimedMessage extends BaseMessage {
  type: 'zone_claimed';
  data: {
    zoneId: string;
    claimedBy: string;
    username: string;
    claimedAt: string;
  };
}

export interface ZoneUnclaimedMessage extends BaseMessage {
  type: 'zone_unclaimed';
  data: {
    zoneId: string;
    previousOwner: string;
  };
}

// Private Area WebSocket message types
export interface PrivateAreaUpdateMessage extends BaseMessage {
  type: 'private_area_update';
  data: {
    areaId: string;
    action: 'player_entered' | 'player_left' | 'closed' | 'opened';
    userId?: string;
    area: PrivateArea;
  };
}

export interface PlayerEnteredPrivateAreaMessage extends BaseMessage {
  type: 'player_entered_private_area';
  data: {
    userId: string;
    areaId: string;
  };
}

export interface PlayerLeftPrivateAreaMessage extends BaseMessage {
  type: 'player_left_private_area';
  data: {
    userId: string;
    areaId: string;
  };
}

export interface PrivateAreaDoorUpdateMessage extends BaseMessage {
  type: 'private_area_door_update';
  data: {
    areaId: string;
    isDoorOpen: boolean;
  };
}

// API Response types for zones
export interface GetHomeZonesResponse {
  zones: HomeZone[];
}

// ========================================
// PLATFORM ROLES & ORGANIZATIONS DTOs
// ========================================

/**
 * One member of an island: a stored `RoomPermission` plus the little of its user
 * the member list shows. Deactivated users keep their row (§3 active permission)
 * and are rendered greyed out rather than hidden.
 */
export interface RoomMemberSummary {
  userId: string;
  username: string;
  avatar: string;
  role: RoomRole;
  /** Guest expiry; null for admins and residents. A past date means lapsed. */
  expiresAt: string | null;
  isActive: boolean;
}

/** Payload of the `room_role_changed` event; `role: null` means removed. */
export interface RoomRoleChangedEvent {
  roomId: string;
  userId: string;
  role: RoomRole | null;
  expiresAt: string | null;
}

/** An organization as shown in pickers and console group headers. */
export interface OrganizationSummary {
  id: string;
  name: string;
  activeIslandCount: number;
}

/** A pending invitation as listed to island admins and superadmins (never the token). */
export interface InvitationSummary {
  id: string;
  email: string | null;
  /** null for a platform invitation (an account with no island). */
  roomId: string | null;
  roomName: string | null;
  role: RoomRole | null;
  guestDays: GuestDays | null;
  invitedByUsername: string;
  createdAt: string;
  expiresAt: string;
}

/** Public preview of an invitation link, shown before the invitee signs up. */
export interface InvitationPreview {
  roomName: string | null;
  organizationName: string | null;
  role: RoomRole | null;
  guestDays: GuestDays | null;
  invitedByUsername: string;
  expiresAt: string;
  /** True when the invited address already has an account, so it must log in. */
  emailKnown: boolean;
  /**
   * The invited address is deliberately **not** part of this DTO. The preview is
   * public — anyone holding the link can fetch it without signing in — so
   * returning the mailbox would hand a forwarded or leaked link the one fact it
   * lacks. The registration form says the account will use "the address this
   * invitation was sent to" and the server fills it in from the invitation;
   * an existing account proves the address by logging in, and the strict-match
   * failure names nothing either (§3.1 invariant 4, §6.1 forwarded-link risk).
   */
}

/** What accepting an invitation grants. Null everywhere for a platform invitation. */
export interface InvitationAcceptResult {
  roomId: string | null;
  /** The *effective* role afterwards: accepting never downgrades an existing one. */
  role: RoomRole | null;
  /** Set only for guests, who are always time-boxed (§3.1 invariant 3). */
  expiresAt: string | null;
}

/** A row of the console Users tab. Presence comes separately from PresenceSnapshot. */
export interface AdminUserRow {
  id: string;
  username: string;
  email: string;
  platformRole: PlatformRole;
  isActive: boolean;
  /** When the account was deactivated; null while it is active. */
  deactivatedAt: string | null;
  /** Derived from active island permissions; never stored. */
  organizations: Array<{ id: string; name: string }>;
  islandCount: number;
  lastLogin: string | null;
  createdAt: string;
}

/** A row of the console Islands tab. */
export interface AdminIslandRow {
  id: string;
  name: string;
  organization: { id: string; name: string };
  adminCount: number;
  residentCount: number;
  guestCount: number;
  createdAt: string;
  /** Historical fact only: creating an island grants nothing (spec §4.1). */
  creatorUsername: string;
}

/** One snapshot of who is online and where, read from the in-memory state. */
export interface PresenceSnapshot {
  users: Array<{ userId: string; roomId: string; status: PlayerStatus }>;
  islands: Array<{ roomId: string; count: number }>;
}

/** One island a user holds a permission on, as listed in the console user modal. */
export interface AdminUserIsland {
  roomId: string;
  name: string;
  organization: { id: string; name: string };
  role: RoomRole;
  expiresAt: string | null;
  /** True when the permission is *active* per §3: live user, live island, unexpired guest. */
  active: boolean;
}

/** One user as opened in the console: the row plus every permission they hold. */
export interface AdminUserDetail extends AdminUserRow {
  islands: AdminUserIsland[];
}

/** A member of an island as listed in the console island modal; deactivated ones stay visible. */
export interface AdminIslandMember {
  userId: string;
  username: string;
  avatar: string;
  role: RoomRole;
  expiresAt: string | null;
  isActive: boolean;
}

/** A pending invitation on an island, read-only in the console (never the token). */
export interface AdminPendingInvitation {
  id: string;
  email: string | null;
  role: RoomRole | null;
  guestDays: GuestDays | null;
  expiresAt: string;
  invitedByUsername: string;
}

/**
 * One island as opened in the console. Soft-deleted islands stay visible to
 * superadmins, so `isActive` is part of the payload.
 */
export interface AdminIslandDetail extends AdminIslandRow {
  isActive: boolean;
  members: AdminIslandMember[];
  pendingInvitations: AdminPendingInvitation[];
}
